import { Doc } from '@dadohr/shared/domainTypes/document';
import { LessonlyIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/lessonly';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useRef, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import {
  isInvalidLessonlyCredentialsError,
  removeLessonlyConfig,
  setupLessonlyIntegration,
  useLessonlyConfig
} from './service';

const NAME = 'Lessonly';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<LessonlyIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { subdomain, apiKey } = config.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.lessonly.setupInfo.p1')}
        </Alert>
        <IntegrationData>
          <strong>{t('Subdomain')}</strong>
          <div>{subdomain || ''}</div>
          <strong>{t('API Key')}</strong>
          <div>{masked(apiKey)}</div>
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeLessonlyConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

const SetupDialog = ({
  config,
  open,
  onClose,
  onFailure,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<LessonlyIntegrationConfig>) => {
  const { t } = useUnsafeTranslation();
  const isSetup = !!config?.data.active && !!config?.data.apiKey;
  const previousConfig = useRef<Doc<LessonlyIntegrationConfig> | null>();

  const [state, setState] = useState<{ subdomain: string; apiKey: string }>({
    subdomain: config?.data.subdomain || '',
    apiKey: ''
  });
  const [invalidCredentials, setInvalidCredentials] = useState(false);

  //This is a bit of a hack to detect when the config active status changes so we can show the modal dialog
  //here even if the integration is completed on another browser tab.
  useEffect(() => {
    if (
      previousConfig.current?.data &&
      config?.data.active &&
      !previousConfig.current?.data.active
    ) {
      onSuccessfulIntegration();
    }
    previousConfig.current = config;
  }, [config, onSuccessfulIntegration]);

  if (!config) {
    return null;
  }

  if (isSetup) {
    return (
      <ExistingSetupDialog config={config} open={open} onClose={onClose} />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.lessonly.setupInfo.p1')}
        </Alert>
        {invalidCredentials && (
          <Alert severity="error">
            {t('Lessonly rejected the subdomain or API key')}
          </Alert>
        )}

        <FlexContainerVertical spacing={2}>
          <CustomInputLabel label={t('Subdomain')} fullWidth>
            <TextField
              variant="outlined"
              value={state.subdomain}
              onChange={(ev) =>
                setState((s) => ({ ...s, subdomain: ev.target.value }))
              }
              helperText={
                <>
                  {t('e.g if you access Lessonly via')} https://acme.lessonly.com
                  {t(', your subdomain is')} <b>acme</b>
                </>
              }
              fullWidth
            />
          </CustomInputLabel>
          <CustomInputLabel label={t('API Key')} fullWidth>
            <TextField
              variant="outlined"
              value={state.apiKey}
              type="password"
              onChange={(ev) =>
                setState((s) => ({ ...s, apiKey: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FlexContainerVertical>
      </Content>
      <DialogActionsWithSlots
        right={
          <FlexContainer justifyContent="flex-end">
            <Button onClick={onClose}>{t('Cancel')}</Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              pending={`${t('Saving')}...`}
              disabled={!state.subdomain.trim() || !state.apiKey}
              onClick={() => {
                setInvalidCredentials(false);
                return setupLessonlyIntegration(config.data.orgId, {
                  subdomain: state.subdomain.trim(),
                  apiKey: state.apiKey
                }).catch((err) => {
                  if (isInvalidLessonlyCredentialsError(err)) {
                    setInvalidCredentials(true);
                  } else {
                    onFailure(err instanceof Error ? err.message : undefined);
                  }
                });
              }}
            >
              {t('Set up')}
            </ButtonWithPromise>
          </FlexContainer>
        }
      />
    </Dialog>
  );
};

export const LessonlySettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useLessonlyConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/lessonly.png"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
